import React, { useState, useEffect, useContext } from 'react';
import { BookingContext } from '../../context/BookingContext';
import { firestore } from '../../firebase/config';
import './PractitionerSelection.css';

const PractitionerSelection = ({ presetPractitionerId }) => {
  const { bookingData, updateBookingData, setCurrentStep } = useContext(BookingContext);
  const [practitioners, setPractitioners] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Charger la liste des praticiens actifs
  useEffect(() => {
    const fetchPractitioners = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Si un ID de praticien présélectionné est fourni, ne récupérer que ce praticien
        if (presetPractitionerId) {
          const practitionerDoc = await firestore
            .collection('practitioners')
            .doc(presetPractitionerId)
            .get();
            
          if (practitionerDoc.exists) {
            const practitionerData = practitionerDoc.data();
            if (practitionerData.isActive !== false) {
              const practitioner = {
                id: practitionerDoc.id,
                ...practitionerData
              };
              setPractitioners([practitioner]);
              
              // Sélectionner automatiquement ce praticien
              setSelectedPractitioner(practitioner.id);
              
              if (!bookingData.practitioner) {
                handlePractitionerSelect(practitioner);
              }
            } else {
              setError("Ce praticien n'est pas disponible actuellement.");
              setPractitioners([]);
            }
          } else {
            setError("Praticien non trouvé.");
            setPractitioners([]);
          }
        } else {
          // Sinon, récupérer tous les praticiens actifs
          const snapshot = await firestore
            .collection('practitioners')
            .where('isActive', '==', true)
            .get();
          
          if (snapshot.empty) {
            setError("Aucun praticien disponible actuellement.");
            setPractitioners([]);
          } else {
            const practitionersList = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            setPractitioners(practitionersList);
            
            // Si un praticien était déjà sélectionné, vérifier s'il est toujours dans la liste
            if (bookingData.practitioner) {
              const practitionerStillExists = practitionersList.some(
                p => p.id === bookingData.practitioner.id
              );
              
              if (!practitionerStillExists) {
                // Réinitialiser si le praticien n'existe plus
                updateBookingData({ practitioner: null });
                setSelectedPractitioner(null);
              } else {
                setSelectedPractitioner(bookingData.practitioner.id);
              }
            }
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des praticiens:', error);
        setError("Une erreur est survenue lors du chargement des praticiens.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPractitioners();
  }, [presetPractitionerId, bookingData.practitioner, updateBookingData]);
  
  // Initialiser la sélection si un praticien est déjà dans le bookingData
  useEffect(() => {
    if (bookingData.practitioner && !selectedPractitioner) {
      setSelectedPractitioner(bookingData.practitioner.id);
    }
  }, [bookingData.practitioner, selectedPractitioner]);
  
  const handlePractitionerSelect = (practitioner) => {
    setSelectedPractitioner(practitioner.id);
    
    // Stocker l'objet praticien complet pour pouvoir accéder à ses propriétés plus tard
    updateBookingData({
      practitioner: {
        id: practitioner.id,
        name: practitioner.name,
        title: practitioner.title,
        specialties: practitioner.specialties,
        profileImage: practitioner.profileImage || '/images/default-avatar.png'
      }
    });
  };
  
  const handleNext = () => {
    if (selectedPractitioner) {
      setCurrentStep(2); // Passer à la sélection du service
    }
  };

  // Formatter l'adresse du cabinet
  const formatAddress = (practitioner) => {
    if (!practitioner.address) return null;
    
    const { street, zipCode, city } = practitioner.address;
    if (!street && !city) return null;
    
    let formattedAddress = street || '';
    if (zipCode || city) {
      formattedAddress += formattedAddress ? `, ${zipCode} ${city}`.trim() : `${zipCode} ${city}`.trim();
    }
    
    return formattedAddress;
  };
  
  if (loading) {
    return (
      <div className="practitioner-selection loading-state">
        <div className="loading-spinner"></div>
        <p>Chargement des praticiens...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="practitioner-selection error-state">
        <div className="error-message">{error}</div>
      </div>
    );
  }
  
  if (practitioners.length === 0) {
    return (
      <div className="practitioner-selection empty-state">
        <p>Aucun praticien disponible actuellement.</p>
      </div>
    );
  }
  
  // Si un praticien est présélectionné et déjà sélectionné, passer automatiquement à l'étape suivante
  if (presetPractitionerId && selectedPractitioner === presetPractitionerId && bookingData.practitioner) {
    // Utiliser setTimeout pour éviter des problèmes de rendu
    setTimeout(() => {
      setCurrentStep(2);
    }, 0);
    
    return (
      <div className="practitioner-selection loading-state">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }
  
  return (
    <div className="practitioner-selection">
      <h2>Choisissez votre praticien</h2>
      
      {bookingData.practitioner && (
        <div className="practitioner-preselection-notice">
          <p>
            Un praticien a été présélectionné mais vous pouvez en choisir un autre si vous le souhaitez.
          </p>
        </div>
      )}
      
      <div className="practitioners-grid">
        {practitioners.map(practitioner => {
          const address = formatAddress(practitioner);
          
          return (
            <div
              key={practitioner.id}
              className={`practitioner-card ${selectedPractitioner === practitioner.id ? 'selected' : ''}`}
              onClick={() => handlePractitionerSelect(practitioner)}
            >
              <img 
                src={practitioner.profileImage || '/images/default-avatar.png'} 
                alt={practitioner.name} 
                className="practitioner-image"
              />
              <div className="practitioner-info">
                <h3>{practitioner.name}</h3>
                <p className="practitioner-title">{practitioner.title}</p>
                {address && (
                  <p className="practitioner-address">
                    <span className="address-icon">📍</span> {address}
                  </p>
                )}
                {practitioner.specialties && practitioner.specialties.length > 0 && (
                  <div className="practitioner-specialties">
                    {practitioner.specialties.map(specialty => (
                      <span key={specialty} className="specialty-tag">{specialty}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="step-navigation">
        <div></div>
        <button
          className={`btn ${!selectedPractitioner ? 'btn-disabled' : ''}`}
          onClick={handleNext}
          disabled={!selectedPractitioner}
        >
          Suivant
        </button>
      </div>
    </div>
  );
};

export default PractitionerSelection;